import { type RefObject, useState } from 'react';
import { Alert, Button } from '@/design-system';
import { ApiError } from '@/lib/api';
import type { Answers, Catalogue } from '@/lib/intake/catalogue';
import { type AttachedFile, kilobytes } from '@/lib/intake/documents';
import { applies, type Evaluation, isRequired } from '@/lib/intake/rules';
import { UnsavedChanges } from '@/lib/intake/useDraft';
import { displayAnswer } from './answers';
import { COPY } from './copy';
import { SummaryActions } from './SummaryActions';
import styles from './Journey.module.css';

export interface ReviewStepProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  applicationId: string;
  catalogue: Catalogue;
  answers: Answers;
  evaluation: Evaluation;
  files: readonly AttachedFile[];
  editable: boolean;
  submissionMode: 'lenient' | 'strict';
  /** Sent before: the button says "Send again". */
  resending: boolean;
  canClear: boolean;
  busy: boolean;
  onEdit: (step: number) => void;
  onBack: () => void;
  onClear: () => void;
  onSubmit: () => Promise<void>;
}

/**
 * Step 10: every question by section with its answer, "Not answered" or "Skipped",
 * and every document requirement. What is complete comes from the same rules the
 * server applies at submission.
 */
export function ReviewStep({ headingRef, ...props }: ReviewStepProps) {
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const incomplete = props.catalogue.sections
    .map((section, index) => ({ section, step: index + 1 }))
    .filter((_, index) => props.evaluation.sections[index]?.complete === false);

  async function send() {
    setSending(true);
    setRefusal(null);
    try {
      await props.onSubmit();
    } catch (e) {
      setRefusal(
        e instanceof UnsavedChanges ? COPY.unsavedBeforeSend : e instanceof ApiError ? e.message : COPY.sendFailed,
      );
      setSending(false);
    }
  }

  return (
    <section className={styles.card} aria-labelledby="step-heading">
      <h1 id="step-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        {COPY.reviewTitle}
      </h1>
      <Alert tone={incomplete.length === 0 ? 'success' : 'info'}>
        {COPY.incomplete(incomplete.length, props.editable ? props.submissionMode : 'sent')}
        {incomplete.length > 0 && props.editable && (
          <>
            <p className={styles.gapLead}>{COPY.stillToComplete}</p>
            <ul className={styles.gapList}>
              {incomplete.map(({ section, step }) => (
                <li key={section.id}>
                  <button type="button" className={styles.gapLink} onClick={() => props.onEdit(step)}>
                    {COPY.goTo(step, section.title)}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Alert>
      {props.catalogue.sections.map((section, index) => (
        <section key={section.id} aria-labelledby={`review-${section.id}`} className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 id={`review-${section.id}`} className={styles.subtitle}>
              {index + 1}. {section.title}
            </h2>
            {props.editable && (
              <Button variant="ghost" size="sm" onClick={() => props.onEdit(index + 1)}>
                Change<span className={styles.visuallyHidden}> {section.title}</span>
              </Button>
            )}
          </div>
          <dl className={styles.answers}>
            {section.fields
              .filter((field) => applies(field, props.answers))
              .map((field) => {
                const shown = displayAnswer(field, props.answers[field.id]);
                const required = isRequired(field, props.answers);
                return (
                  <div key={field.id} className={styles.answer}>
                    <dt>{field.label}</dt>
                    <dd className={shown === null ? (required ? styles.gap : styles.skipped) : undefined}>
                      {shown ?? (required ? COPY.notAnswered : COPY.skipped)}
                    </dd>
                  </div>
                );
              })}
          </dl>
        </section>
      ))}
      <section aria-labelledby="review-documents" className={styles.reviewSection}>
        <h2 id="review-documents" className={styles.subtitle}>
          {COPY.documentsTitle}
        </h2>
        <dl className={styles.answers}>
          {props.catalogue.documents.map((doc) => {
            const files = props.files.filter((f) => f.requirementId === doc.id);
            return (
              <div key={doc.id} className={styles.answer}>
                <dt>{doc.title}</dt>
                {files.length === 0 ? (
                  <dd className={styles.skipped}>{COPY.notProvided}</dd>
                ) : (
                  files.map((f) => (
                    <dd key={f.fileId}>
                      {f.fileName} ({kilobytes(f.sizeBytes)}){f.scanStatus === 'CLEAN' ? '' : ` — ${COPY.scan[f.scanStatus]}`}
                    </dd>
                  ))
                )}
              </div>
            );
          })}
        </dl>
      </section>
      <SummaryActions applicationId={props.applicationId} />
      {confirmingClear && (
        <Alert tone="danger" title={COPY.clearEverything}>
          {COPY.startOverConfirm}
          <span className={styles.inlineActions}>
            <Button size="sm" disabled={props.busy} onClick={props.onClear}>
              {COPY.clearEverything}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingClear(false)}>
              Keep my answers
            </Button>
          </span>
        </Alert>
      )}
      {refusal !== null && (
        <Alert tone="danger" title={COPY.sendFailedTitle}>
          {refusal}
        </Alert>
      )}
      <div className={styles.actions}>
        <Button variant="secondary" onClick={props.onBack}>
          {COPY.back}
        </Button>
        {props.canClear && (
          <Button variant="ghost" disabled={props.busy || sending} onClick={() => setConfirmingClear(true)}>
            {COPY.clearEverything}
          </Button>
        )}
        {props.editable && (
          <Button disabled={props.busy || sending} onClick={() => void send()}>
            {sending ? COPY.sending : props.resending ? COPY.sendAgain : COPY.submit}
          </Button>
        )}
      </div>
    </section>
  );
}
